import { createLabelDescriptionFrom } from "@jsonforms/core";

import {
	pointerSegment,
	schemaAtScope
} from "./schema";
import type {
	JsonSchemaDefinition,
	ValidationError
} from "./types";
import { isRecord } from "./values";

function requiredPropertySchema(
	error: ValidationError,
	propertyScope: string,
	rootSchema?: JsonSchemaDefinition
): JsonSchemaDefinition {
	const parent = isRecord(error.parentSchema) ? error.parentSchema as JsonSchemaDefinition : {};
	const property = schemaAtScope(rootSchema ?? parent, propertyScope, parent);

	if(property.title || !rootSchema) {
		return property;
	}

	// A conditional/composition branch may require a property whose title is
	// declared on its enclosing object. Keep resolution on that schema branch.
	let scope = error.schemaPath.replace(/\/required$/, "");

	while(scope.startsWith("#")) {
		const candidate = schemaAtScope(rootSchema, `${scope}${propertyScope.slice(1)}`);

		if(candidate.title) {
			return candidate;
		}

		const enclosing = scope.replace(/\/(?:then|else|(?:allOf|anyOf|oneOf)\/\d+|dependencies\/[^/]+)$/, "");

		if(enclosing === scope) {
			break;
		}

		scope = enclosing;
	}

	return property;
}

/** Change default required wording without replacing authored errorMessage text. */
export function formatValidationErrors(
	errors: ValidationError[],
	rootSchema?: JsonSchemaDefinition
): ValidationError[] {
	return errors.map(error => {
		if(error.keyword !== "required" || typeof error.params.missingProperty !== "string"
			|| !error.message?.startsWith("must have required property")) {
			return error;
		}

		const property = error.params.missingProperty;
		const scope = `#/properties/${pointerSegment(property)}`;
		const schema = requiredPropertySchema(error, scope, rootSchema);
		const label = createLabelDescriptionFrom({
			type: "Control",
			scope
		}, schema).text;

		return {
			...error,
			message: `${label || property || "This field"} is required`
		};
	});
}
